import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  collection, getDocs, addDoc, query, where, writeBatch, doc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getDocument } from '../utils/matchHelpers';
import { useAuth } from '../context/AuthContext';
import AppDialog from '../components/AppDialog';

const DEFAULT_QUICK_MATCH_FORM = {
  game_type: 'singles', team1_name: '', team2_name: '',
  team1_p1_id: '', team1_p2_id: '', team2_p1_id: '', team2_p2_id: '',
  sets_to_win: 1, points_to_win: 11, court_number: 1,
};

export default function Dashboard() {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ tournaments: [], liveMatches: [], players: [] });
  const [loading, setLoading] = useState(true);
  const [showQuickMatch, setShowQuickMatch] = useState(false);
  const [quickMatchForm, setQuickMatchForm] = useState(DEFAULT_QUICK_MATCH_FORM);
  const [savingQuickMatch, setSavingQuickMatch] = useState(false);
  const [eraseDialog, setEraseDialog] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [appDialog, setAppDialog] = useState(null);
  const showAlert = (title, message) => setAppDialog({ title, message });

  useEffect(() => {
    Promise.all([
      getDocs(collection(db, 'tournaments')),
      getDocs(query(collection(db, 'matches'), where('status', '==', 'active'))),
      getDocs(collection(db, 'players')),
    ]).then(([tSnap, lSnap, pSnap]) => {
      setStats({
        tournaments: tSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        liveMatches: lSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        players: pSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      });
    }).finally(() => setLoading(false));
  }, []);

  const handleEraseAll = async () => {
    setErasing(true);
    try {
      for (const col of ['tournaments', 'teams', 'matches', 'points', 'players']) {
        const snap = await getDocs(collection(db, col));
        if (snap.empty) continue;
        const batch = writeBatch(db);
        snap.docs.forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
      setEraseDialog(false);
      setStats({ tournaments: [], liveMatches: [], players: [] });
    } catch (err) {
      showAlert('Error', err.message || 'Error erasing data');
    } finally { setErasing(false); }
  };

  const handleCreateQuickMatch = async (e) => {
    e.preventDefault();
    if (!quickMatchForm.team1_p1_id || !quickMatchForm.team2_p1_id) {
      showAlert('Missing Players', 'Please select at least one player for each team.');
      return;
    }
    setSavingQuickMatch(true);
    try {
      const isDoubles = quickMatchForm.game_type === 'doubles';
      const [p1, p2, p3, p4] = await Promise.all([
        getDocument('players', quickMatchForm.team1_p1_id),
        isDoubles && quickMatchForm.team1_p2_id ? getDocument('players', quickMatchForm.team1_p2_id) : null,
        getDocument('players', quickMatchForm.team2_p1_id),
        isDoubles && quickMatchForm.team2_p2_id ? getDocument('players', quickMatchForm.team2_p2_id) : null,
      ]);

      const team1Name = quickMatchForm.team1_name.trim() || (p2 ? `${p1.name} & ${p2.name}` : p1.name);
      const team2Name = quickMatchForm.team2_name.trim() || (p4 ? `${p3.name} & ${p4.name}` : p3.name);

      const teamData = { wins: 0, losses: 0, points_for: 0, points_against: 0, tournament_id: null };
      const [t1Ref, t2Ref] = await Promise.all([
        addDoc(collection(db, 'teams'), { ...teamData, name: team1Name, player1_id: quickMatchForm.team1_p1_id, player2_id: isDoubles ? quickMatchForm.team1_p2_id || null : null }),
        addDoc(collection(db, 'teams'), { ...teamData, name: team2Name, player1_id: quickMatchForm.team2_p1_id, player2_id: isDoubles ? quickMatchForm.team2_p2_id || null : null }),
      ]);

      const matchRef = await addDoc(collection(db, 'matches'), {
        tournament_id: null, game_type: quickMatchForm.game_type,
        team1_id: t1Ref.id, team2_id: t2Ref.id,
        round: 1, match_order: 1,
        points_to_win: quickMatchForm.points_to_win,
        sets_to_win: quickMatchForm.sets_to_win,
        team1_sets: 0, team2_sets: 0,
        court_number: quickMatchForm.court_number,
        status: 'scheduled', winner_team_id: null,
        team1_score: 0, team2_score: 0,
        current_server_team: t1Ref.id,
        server_number: 2, is_first_serve: true,
        current_server_player_id: null, court_positions: '{}',
        scheduled_at: null, started_at: null, completed_at: null,
        created_at: new Date().toISOString(),
      });

      setShowQuickMatch(false);
      setQuickMatchForm(DEFAULT_QUICK_MATCH_FORM);
      navigate(`/matches/${matchRef.id}/live`);
    } catch (err) {
      showAlert('Error', err.message || 'Error creating match');
    } finally { setSavingQuickMatch(false); }
  };

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: '#7f8c8d' }}>Loading...</div>;

  const activeTournaments = stats.tournaments.filter(t => t.status === 'active').length;
  const isDoubles = quickMatchForm.game_type === 'doubles';
  const { team1_p1_id, team1_p2_id, team2_p1_id, team2_p2_id } = quickMatchForm;

  return (
    <div className="container" style={{ padding: '32px 20px' }}>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '26px', fontWeight: '800' }}>Welcome back, {admin?.email}! 👋</h1>
        <p style={{ color: '#7f8c8d', marginTop: '4px' }}>Here's what's happening with your tournaments</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {[
          { label: 'Total Tournaments', value: stats.tournaments.length, icon: '🏆', color: '#27ae60', link: '/tournaments' },
          { label: 'Active Tournaments', value: activeTournaments, icon: '▶️', color: '#2980b9', link: '/tournaments' },
          { label: 'Live Matches', value: stats.liveMatches.length, icon: '🔴', color: '#e74c3c', link: '/tournaments' },
          { label: 'Registered Players', value: stats.players.length, icon: '👤', color: '#8e44ad', link: '/players' },
        ].map((s, i) => (
          <Link key={i} to={s.link} style={{ textDecoration: 'none' }}>
            <div className="card animate-fadein" style={{ animationDelay: `${i * 0.05}s`, cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>{s.icon}</div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '13px', color: '#7f8c8d', marginTop: '4px', fontWeight: '500' }}>{s.label}</div>
            </div>
          </Link>
        ))}
      </div>

      <div className="dashboard-cols">
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#e74c3c', display: 'inline-block', animation: 'pulse 1s infinite' }} />
              Live Matches
            </h2>
            <Link to="/tournaments" className="btn btn-ghost btn-sm">View All</Link>
          </div>
          {stats.liveMatches.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', color: '#7f8c8d', fontSize: '14px' }}>No live matches right now</div>
          ) : stats.liveMatches.map(m => (
            <Link key={m.id} to={`/matches/${m.id}/live`} style={{ textDecoration: 'none' }}>
              <div style={{ padding: '12px', border: '1.5px solid #e8f8f0', borderRadius: '8px', marginBottom: '8px', cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#27ae60'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e8f8f0'}>
                <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '6px' }}>Court {m.court_number}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600' }}>{m.team1_id}</span>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <span style={{ fontSize: '20px', fontWeight: '800', color: '#27ae60' }}>{m.team1_score}</span>
                    <span style={{ color: '#bdc3c7' }}>—</span>
                    <span style={{ fontSize: '20px', fontWeight: '800', color: '#2980b9' }}>{m.team2_score}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '700' }}>🏆 Tournaments</h2>
            <Link to="/tournaments" className="btn btn-ghost btn-sm">View All</Link>
          </div>
          {stats.tournaments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', color: '#7f8c8d', fontSize: '14px' }}>
              No tournaments yet. <Link to="/tournaments" style={{ color: '#27ae60' }}>Create one!</Link>
            </div>
          ) : stats.tournaments.slice(0, 5).map(t => (
            <Link key={t.id} to={`/tournaments/${t.id}`} style={{ textDecoration: 'none' }}>
              <div style={{ padding: '10px 0', borderBottom: '1px solid #f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>{t.name}</div>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '2px' }}>{t.type} · {t.format?.replace('_', ' ')}</div>
                </div>
                <span className={`badge badge-${t.status}`}>{t.status}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginTop: '20px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>Quick Actions</h2>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Link to="/tournaments" className="btn btn-primary">+ New Tournament</Link>
          <Link to="/players" className="btn btn-secondary">+ Add Player</Link>
          <button className="btn btn-outline" onClick={() => setShowQuickMatch(true)}>🏓 Quick Match</button>
          <Link to="/leaderboard" className="btn btn-outline">View Leaderboard</Link>
          <button className="btn" onClick={() => setEraseDialog(true)}
            style={{ background: 'rgba(231,76,60,0.1)', color: '#e74c3c', border: '1.5px solid #e74c3c', marginLeft: 'auto' }}>
            🗑 Erase All Data
          </button>
        </div>
      </div>

      {eraseDialog && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEraseDialog(false)}>
          <div className="modal animate-fadein" style={{ maxWidth: '420px', width: '100%' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>⚠️</div>
              <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#e74c3c', marginBottom: '8px' }}>Erase All Data</h2>
              <p style={{ color: '#7f8c8d', fontSize: '14px', lineHeight: '1.6' }}>
                This will permanently delete <strong>all tournaments, matches, teams, players, and point history</strong>. Admin accounts will not be affected.
              </p>
              <p style={{ color: '#e74c3c', fontSize: '13px', fontWeight: '600', marginTop: '12px' }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEraseDialog(false)} disabled={erasing}>Cancel</button>
              <button className="btn" onClick={handleEraseAll} disabled={erasing} style={{ background: '#e74c3c', color: '#fff', border: 'none' }}>
                {erasing ? 'Erasing...' : '🗑 Yes, Erase Everything'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showQuickMatch && (() => {
        const taken = (exclude) => new Set([team1_p1_id, team1_p2_id, team2_p1_id, team2_p2_id].filter(id => id && id !== exclude));
        const opts = (excludeSlot) => stats.players.filter(p => !taken(excludeSlot).has(p.id));
        return (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowQuickMatch(false)}>
            <div className="modal animate-fadein" style={{ maxWidth: '500px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ flexShrink: 0, paddingBottom: '4px' }}>
                <h2 style={{ marginBottom: '4px' }}>🏓 Quick Match</h2>
                <p style={{ color: '#7f8c8d', fontSize: '13px', margin: 0 }}>Start a standalone match without a tournament.</p>
              </div>
              <form onSubmit={handleCreateQuickMatch} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px', paddingTop: '16px' }}>
                  <div className="form-group">
                    <label>Game Type</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {['singles', 'doubles'].map(gt => (
                        <button key={gt} type="button"
                          onClick={() => setQuickMatchForm(f => ({ ...f, game_type: gt, team1_p2_id: '', team2_p2_id: '' }))}
                          style={{ flex: 1, padding: '10px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', background: quickMatchForm.game_type === gt ? '#27ae60' : '#f0f4f8', color: quickMatchForm.game_type === gt ? '#fff' : '#7f8c8d', border: 'none', cursor: 'pointer' }}>
                          {gt === 'singles' ? '1v1 Singles' : '2v2 Doubles'}
                        </button>
                      ))}
                    </div>
                  </div>
                  {[{ teamKey: 'team1', label: 'Team 1', color: '#27ae60', p1: 'team1_p1_id', p2: 'team1_p2_id', name: 'team1_name' },
                    { teamKey: 'team2', label: 'Team 2', color: '#2980b9', p1: 'team2_p1_id', p2: 'team2_p2_id', name: 'team2_name' }].map(({ label, color, p1, p2, name }) => (
                    <div key={label} style={{ background: '#f0f4f8', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color, marginBottom: '8px', textTransform: 'uppercase' }}>{label}</div>
                      <div className="form-group" style={{ marginBottom: '8px' }}>
                        <label>Team Name <span style={{ color: '#bdc3c7', fontWeight: '400' }}>(optional)</span></label>
                        <input className="form-control" type="text" placeholder="Auto-generated" value={quickMatchForm[name]}
                          onChange={e => setQuickMatchForm(f => ({ ...f, [name]: e.target.value }))} />
                      </div>
                      <div className="form-group" style={{ marginBottom: isDoubles ? '8px' : 0 }}>
                        <label>Player 1 *</label>
                        <select className="form-control" required value={quickMatchForm[p1]}
                          onChange={e => setQuickMatchForm(f => ({ ...f, [p1]: e.target.value }))}>
                          <option value="">Select player</option>
                          {opts(quickMatchForm[p1]).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </div>
                      {isDoubles && (
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label>Player 2</label>
                          <select className="form-control" value={quickMatchForm[p2]}
                            onChange={e => setQuickMatchForm(f => ({ ...f, [p2]: e.target.value }))}>
                            <option value="">Select player</option>
                            {opts(quickMatchForm[p2]).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="grid-2">
                    <div className="form-group">
                      <label>Sets</label>
                      <select className="form-control" value={quickMatchForm.sets_to_win}
                        onChange={e => setQuickMatchForm(f => ({ ...f, sets_to_win: +e.target.value }))}>
                        <option value={1}>Single Set</option><option value={2}>Best of 3</option><option value={3}>Best of 5</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Points to Win</label>
                      <select className="form-control" value={quickMatchForm.points_to_win}
                        onChange={e => setQuickMatchForm(f => ({ ...f, points_to_win: +e.target.value }))}>
                        <option value={11}>11</option><option value={15}>15</option><option value={21}>21</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Court Number</label>
                    <input className="form-control" type="number" min="1" max="20" value={quickMatchForm.court_number}
                      onChange={e => setQuickMatchForm(f => ({ ...f, court_number: +e.target.value }))} />
                  </div>
                </div>
                <div className="modal-footer" style={{ flexShrink: 0, borderTop: '1px solid #f0f4f8', marginTop: '8px', paddingTop: '16px' }}>
                  <button type="button" className="btn btn-ghost" onClick={() => setShowQuickMatch(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={savingQuickMatch}>
                    {savingQuickMatch ? 'Creating...' : '🏓 Start Match'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      <AppDialog dialog={appDialog} onClose={() => setAppDialog(null)} />
    </div>
  );
}
